/**
 * Keyboard wrapper — 가상 키보드 이벤트 (입력 폼 가림 방지).
 *
 * 사용 예 (배송지 입력 / 메시지 작성):
 *   const kbHeight = useKeyboardHeight();
 *   <div style={{ paddingBottom: kbHeight }}>...
 */
import { useEffect, useState } from "react";
import { isNative, nativeWarn } from "./index";

export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!isNative()) return;
    let mounted = true;
    let willShow: (() => void) | null = null;
    let willHide: (() => void) | null = null;

    (async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard");
        const showHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
          if (mounted) setHeight(info.keyboardHeight);
        });
        const hideHandle = await Keyboard.addListener("keyboardWillHide", () => {
          if (mounted) setHeight(0);
        });
        willShow = () => showHandle.remove();
        willHide = () => hideHandle.remove();
      } catch (err) {
        nativeWarn("keyboard:listen", err);
      }
    })();

    return () => {
      mounted = false;
      willShow?.();
      willHide?.();
    };
  }, []);

  return height;
}

export async function hideKeyboard(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    await Keyboard.hide();
  } catch (err) {
    nativeWarn("keyboard:hide", err);
  }
}
